const PRECISION = 10_000;

export const FRACTIONS = {
  '¼': 0.25,
  '½': 0.5,
  '⅝': 0.625,
  '¾': 0.75,
} as const;

export type Fraction = keyof typeof FRACTIONS;

const DECIMAL_TO_FRACTION = Object.fromEntries(
  Object.entries(FRACTIONS).map(([k, v]) => [
    Math.round(v * PRECISION) / PRECISION,
    k,
  ])
) as Record<number, Fraction>;

function getFractionFromDecimal(decimal: number): Fraction | null {
  const rounded = Math.round(decimal * PRECISION) / PRECISION;
  return DECIMAL_TO_FRACTION[rounded] ?? null;
}

function isFractionSymbol(s: string): s is Fraction {
  return Object.prototype.hasOwnProperty.call(FRACTIONS, s);
}

export type MixedFraction = {
  whole: number | null;
  fraction: Fraction | null;
};

export function parseStoredFraction(value: string): MixedFraction {
  const n = Number.parseFloat(String(value ?? '').trim());
  if (!Number.isFinite(n)) {
    return { whole: null, fraction: null };
  }

  const whole = Math.trunc(n);
  const decimal = Math.round((n - whole) * PRECISION) / PRECISION;
  const fraction = getFractionFromDecimal(decimal);

  return fraction ? { whole, fraction } : { whole: n, fraction: null };
}

export function formatStoredFraction({
  whole,
  fraction,
}: MixedFraction): string {
  if (whole === null && fraction === null) {
    return '';
  }

  if (whole === null) {
    return fraction ? String(FRACTIONS[fraction]) : '';
  }

  const value = whole + (fraction ? FRACTIONS[fraction] : 0);
  return String(Math.round(value * PRECISION) / PRECISION);
}

export function formatFractionDisplay(mixed: MixedFraction): string;
export function formatFractionDisplay(stored: string): string;
export function formatFractionDisplay(input: MixedFraction | string): string {
  const mixed =
    typeof input === 'string' ? parseStoredFraction(input.trim()) : input;
  const { whole, fraction } = mixed;
  if (fraction === null) {
    return whole !== null ? String(whole) : '';
  }
  if (whole === null || whole === 0) {
    return fraction;
  }
  return `${whole} ${fraction}`;
}

export function parseFractionInput(raw: string): MixedFraction | null {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { whole: null, fraction: null };
  }

  const match = /^(\d*)\s*([¼½⅝¾])?$/.exec(trimmed);
  if (!match) {
    return null;
  }

  const [, whole, fraction] = match;

  return {
    whole: whole ? Number(whole) : 0,
    fraction: fraction && isFractionSymbol(fraction) ? fraction : null,
  };
}
